<template>
	<button
		:class="buttonClasses"
		:disabled="disabled"
		type="button"
		@click="onClick"
	>
		<span class="teamingButton__contentContainer">
			<slot name="leftIcon" v-if="leftIcon || $slots.leftIcon">
				<FontAwesomeIcon
					:icon="leftIcon"
					:class="leftIconClasses"
				/>
			</slot>
			<span :class="textLabelClasses" v-if="$slots.default">
				<slot></slot>
			</span>
			<slot name="rightIcon" v-if="rightIcon || $slots.rightIcon">
				<FontAwesomeIcon
					:icon="rightIcon"
					:class="rightIconClasses"
				/>
			</slot>
		</span>
	</button>
</template>

<script>
import { TeamingButtonType, TeamingButtonSize } from "@teaming/common-enums";

export default {
	name: "TeamingButton",
	emits: [ "buttonPress", ],
	props: {
		type: {
			type: String,
			validator( value ) {
				return Object.keys( TeamingButtonType ).indexOf( value ) !== -1;
			},
			default: TeamingButtonType.PRIMARY,
		},
		size: {
			type: String,
			validator( value ) {
				return Object.keys( TeamingButtonSize ).indexOf( value ) !== -1;
			},
			default: TeamingButtonSize.MEDIUM,
		},
		destructive: {
			type: Boolean,
			default: false,
		},
		leftIcon: {
			type: Array,
			default: () => undefined,
		},
		rightIcon: {
			type: Array,
			default: () => undefined,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		hasLabelContent() {
			return !!this.$slots.default;
		},
		hasRightIcon() {
			return !!this.rightIcon || this.$slots.rightIcon;
		},
		buttonClasses() {
			return {
				teamingButton: true,
				"teamingButton--medium": this.size === TeamingButtonSize.MEDIUM,
				"teamingButton--destructive": this.destructive,
				"teamingButton--small": this.size === TeamingButtonSize.SMALL,
				"teamingButton--tiny": this.size === TeamingButtonSize.TINY,
				"teamingButton--primary": this.type === TeamingButtonType.PRIMARY,
				"teamingButton--secondary": this.type === TeamingButtonType.SECONDARY,
				"teamingButton--tertiary": this.type === TeamingButtonType.TERTIARY,
				"teamingButton--quaternary": this.type === TeamingButtonType.QUATERNARY,
				"text-ms1": this.size === TeamingButtonSize.MEDIUM,
				"text-default": this.size === TeamingButtonSize.SMALL,
				"text-ms-1": this.size === TeamingButtonSize.TINY,
			};
		},
		textLabelClasses() {
			return {
				"text-ms-1": this.size === TeamingButtonSize.TINY,
				"text-default": this.size === TeamingButtonSize.SMALL,
				"text-ms1": this.size === TeamingButtonSize.MEDIUM,
				teamingButton__labelContent: true, // eslint-disable-line camelcase
			};
		},
		/* eslint-disable camelcase */
		leftIconClasses() {
			return {
				teamingButton__icon: true,
				"teamingButton__icon--left": true,
				"teamingButton__iconMedium--spacingToRight": this.size === TeamingButtonSize.MEDIUM && ( this.hasLabelContent || this.hasRightIcon ),
				"teamingButton__iconSmall--spacingToRight": this.size === TeamingButtonSize.SMALL && ( this.hasLabelContent || this.hasRightIcon ),
				"teamingButton__iconTiny--spacingToRight": this.size === TeamingButtonSize.TINY && ( this.hasLabelContent || this.hasRightIcon ),
			};
		},
		rightIconClasses() {
			return {
				teamingButton__icon: true,
				"teamingButton__icon--right": true,
				"teamingButton__iconMedium--spacingToLeft": this.size === TeamingButtonSize.MEDIUM && this.hasLabelContent,
				"teamingButton__iconSmall--spacingToLeft": this.size === TeamingButtonSize.SMALL && this.hasLabelContent,
				"teamingButton__iconTiny--spacingToLeft": this.size === TeamingButtonSize.TINY && this.hasLabelContent,
			};
		},
		/* eslint-enable camelcase */
	},
	methods: {
		onClick( e ) {
			this.$emit( "buttonPress", e );
		},
	},
};
</script>

<style lang="scss" scoped>
.teamingButton {
	border-radius: .5rem;
	cursor: pointer;
	outline: none;
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
}

.teamingButton__contentContainer {
	height: 100%;
	width: 100%;
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
}

.teamingButton__labelContent {
	font-style: normal;
	font-weight: 600;
}

.teamingButton__iconMedium--spacingToRight {
	margin-right: .5625rem;
}

.teamingButton__iconMedium--spacingToLeft {
	margin-left: .5625rem;
}

.teamingButton__iconSmall--spacingToRight {
	margin-right: .4rem;
}

.teamingButton__iconTiny--spacingToRight {
	margin-right: .266rem;
}

.teamingButton__iconSmall--spacingToLeft {
	margin-left: .4rem;
}

.teamingButton__iconTiny--spacingToLeft {
	margin-left: .266rem;
}

.teamingButton--medium {
	padding: .5rem .75rem .5rem .75rem;
	min-width: 2.5rem;

	.teamingButton__icon {
		width: 1rem;
		height: 1rem;
	}
}

.teamingButton--small {
	padding: .375rem .5rem .375rem .5rem;
	min-width: 2rem;
	height: 2rem;

	.teamingButton__icon {
		width: 0.875rem;
		height: 0.875rem;
	}
}

.teamingButton--tiny {
	padding: 0.25rem 0.375rem 0.25rem 0.375rem;
	min-width: 1.5rem;
	height: 1.5rem;

	.teamingButton__icon {
		width: 0.583rem;
		height: 0.583rem;
	}
}

.teamingButton--primary {
	color: $primary-button-font-color;
	background: $primary-button-bg-color;
	border: 0.09375rem solid $primary-button-border-color;
	box-shadow: 0rem 0.1875rem .4375rem -.25rem rgba(42, 47, 69, 0.1), 0rem 0.1875rem 0.4375rem -.25rem rgba(42, 47, 69, 0.05);

	&:hover {
		color: $primary-button-font-color--hover;
		background: $primary-button-bg-color--hover;
		border: 0.09375rem solid $primary-button-border-color--hover;
	}

	&:focus {
		color: $primary-button-font-color--focus;
		background: $primary-button-bg-color--focus;
		border: 0.09375rem solid $primary-button-border-color--focus;
		box-shadow: 0rem 0rem 0rem 0.1875rem $button-focus-outline-color;
	}

	&:disabled {
		color: $primary-button-font-color--disabled;
		background: $primary-button-bg-color--disabled;
		border: 0.09375rem solid $primary-button-border-color--disabled;
		cursor: not-allowed;
	}
}

.teamingButton--secondary {
	color: $secondary-button-font-color;
	background: $secondary-button-bg-color;
	border: 0.09375rem solid $secondary-button-border-color;
	box-shadow: 0rem 0.1875rem .4375rem -.25rem rgba(42, 47, 69, 0.1), 0rem 0.1875rem 0.4375rem -.25rem rgba(42, 47, 69, 0.05);

	&:hover {
		color: $secondary-button-font-color--hover;
		background: $secondary-button-bg-color--hover;
		border: 0.09375rem solid $secondary-button-border-color--hover;
	}

	&:focus {
		color: $secondary-button-font-color--focus;
		background: $secondary-button-bg-color--focus;
		border: 0.09375rem solid $secondary-button-border-color--focus;
		box-shadow: 0rem 0rem 0rem 0.1875rem $button-focus-outline-color;
	}

	&:disabled {
		color: $secondary-button-font-color--disabled;
		background: $secondary-button-bg-color--disabled;
		border: 0.09375rem solid $secondary-button-border-color--disabled;
		cursor: not-allowed;
	}
}

.teamingButton--tertiary {
	color: $tertiary-button-font-color;
	background: $tertiary-button-bg-color;
	border: 0.09375rem solid $tertiary-button-border-color;

	&:hover {
		color: $tertiary-button-font-color--hover;
		background: $tertiary-button-bg-color--hover;
		border: 0.09375rem solid $tertiary-button-border-color--hover;
	}

	&:focus {
		color: $tertiary-button-font-color--focus;
		background: $tertiary-button-bg-color--focus;
		border: 0.09375rem solid $tertiary-button-border-color--focus;
		box-shadow: 0rem 0rem 0rem 0.1875rem $button-focus-outline-color;
	}

	&:disabled {
		color: $tertiary-button-font-color--disabled;
		background: $tertiary-button-bg-color--disabled;
		border: 0.09375rem solid $tertiary-button-border-color--disabled;
		cursor: not-allowed;
	}
}

.teamingButton--quaternary {
	color: $quaternary-button-font-color;
	background: $quaternary-button-bg-color;
	border: 0.09375rem solid $quaternary-button-border-color;

	&:hover {
		color: $quaternary-button-font-color--hover;
		background: $quaternary-button-bg-color--hover;
		border: 0.09375rem solid $quaternary-button-border-color--hover;
	}

	&:focus {
		color: $quaternary-button-font-color--focus;
		background: $quaternary-button-bg-color--focus;
		border: 0.09375rem solid $quaternary-button-border-color--focus;
		box-shadow: 0rem 0rem 0rem 0.1875rem $button-focus-outline-color;
	}

	&:disabled {
		color: $quaternary-button-font-color--disabled;
		background: $quaternary-button-bg-color--disabled;
		border: 0.09375rem solid $quaternary-button-border-color--disabled;
		cursor: not-allowed;
	}
}

.teamingButton--destructive {
	&.teamingButton--primary {
		color: $primary-destructive-button-font-color;
		background: $primary-destructive-button-bg-color;
		border: 0.09375rem solid $primary-destructive-button-border-color;

		&:hover {
			color: $primary-destructive-button-font-color--hover;
			background: $primary-destructive-button-bg-color--hover;
			border: 0.09375rem solid $primary-destructive-button-border-color--hover;
		}

		&:focus {
			color: $primary-destructive-button-font-color--focus;
			background: $primary-destructive-button-bg-color--focus;
			border: 0.09375rem solid $primary-destructive-button-border-color--focus;
		}

		&:disabled {
			color: $primary-destructive-button-font-color--disabled;
			background: $primary-destructive-button-bg-color--disabled;
			border: 0.09375rem solid $primary-destructive-button-border-color--disabled;
			cursor: not-allowed;
		}
	}

	&.teamingButton--secondary {
		color: $secondary-destructive-button-font-color;
		background: $secondary-destructive-button-bg-color;
		border: 0.09375rem solid $secondary-destructive-button-border-color;

		&:hover {
			color: $secondary-destructive-button-font-color--hover;
			background: $secondary-destructive-button-bg-color--hover;
			border: 0.09375rem solid $secondary-destructive-button-border-color--hover;
		}

		&:focus {
			color: $secondary-destructive-button-font-color--focus;
			background: $secondary-destructive-button-bg-color--focus;
			border: 0.09375rem solid $secondary-destructive-button-border-color--focus;
		}

		&:disabled {
			color: $secondary-destructive-button-font-color--disabled;
			background: $secondary-destructive-button-bg-color--disabled;
			border: 0.09375rem solid $secondary-destructive-button-border-color--disabled;
			cursor: not-allowed;
		}
	}

	&.teamingButton--tertiary {
		color: $tertiary-destructive-button-font-color;
		background: $tertiary-destructive-button-bg-color;
		border: 0.09375rem solid $tertiary-destructive-button-border-color;

		&:hover {
			color: $tertiary-destructive-button-font-color--hover;
			background: $tertiary-destructive-button-bg-color--hover;
			border: 0.09375rem solid $tertiary-destructive-button-border-color--hover;
		}

		&:focus {
			color: $tertiary-destructive-button-font-color--focus;
			background: $tertiary-destructive-button-bg-color--focus;
			border: 0.09375rem solid $tertiary-destructive-button-border-color--focus;
		}

		&:disabled {
			color: $tertiary-destructive-button-font-color--disabled;
			background: $tertiary-destructive-button-bg-color--disabled;
			border: 0.09375rem solid $tertiary-destructive-button-border-color--disabled;
			cursor: not-allowed;
		}
	}

	&.teamingButton--quaternary {
		color: $quaternary-destructive-button-font-color;
		background: $quaternary-destructive-button-bg-color;
		border: 0.09375rem solid $quaternary-destructive-button-border-color;

		&:hover {
			color: $quaternary-destructive-button-font-color--hover;
			background: $quaternary-destructive-button-bg-color--hover;
			border: 0.09375rem solid $quaternary-destructive-button-border-color--hover;
		}

		&:focus {
			color: $quaternary-destructive-button-font-color--focus;
			background: $quaternary-destructive-button-bg-color--focus;
			border: 0.09375rem solid $quaternary-destructive-button-border-color--focus;
		}

		&:disabled {
			color: $quaternary-destructive-button-font-color--disabled;
			background: $quaternary-destructive-button-bg-color--disabled;
			border: 0.09375rem solid $quaternary-destructive-button-border-color--disabled;
			cursor: not-allowed;
		}
	}
}
</style>
